import MenuIcon from '@mui/icons-material/Menu'
import PersonIcon from '@mui/icons-material/Person'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Link,
  useMediaQuery
} from '@mui/material'
import { useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar'
import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useCustomNavigation } from '../../../hooks/useCustomNavigate'
import { RootState } from '../../../redux/reducers/rootReducer'
import { NavigationEnum, constants, navItems, ROUTES, DECLINED, SELLER, BUYER, VALID } from '../../../utils/constants'
import { NavButton } from './NavButton'
import { MobileDrawer } from '../mobile/MobileDrawer'
import styles from './Header.module.scss'
import Language from '../../shared-components/language'
import useAuthentication from '../../../hooks/useAuthentication'
import NotificationsNavbar from '../../shared-components/NotificationsNavbar'

export const Header = () => {
  const { t } = useTranslation()
  const matchesMobile = useMediaQuery('(max-width:1024px)')
  const { isAuthenticated } = useAuthentication()
  const customNavigate = useCustomNavigation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [selectedButton, setSelectedButton] = useState(1)
  const [hoveredButton, setHoveredButton] = useState(0)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])
  
  useEffect(() => {
    let currentBtnIndex = 1;
    if (location.pathname.startsWith(`/${ROUTES.ASSETS}`)) {
      currentBtnIndex = 2;
    }
    if (location.pathname.startsWith(`/${ROUTES.PLAN_PRICES}`)) {
      currentBtnIndex = 5;
    }
    if (location.pathname.startsWith('/about')) {
      currentBtnIndex = 4;
    }
    if (location.pathname.startsWith(`/${ROUTES.ACCOUNT}`)) {
      currentBtnIndex = 6;
    }
    if (location.pathname.startsWith(`/${ROUTES.LOGIN}`)) {
      currentBtnIndex = 6;
    }
    setSelectedButton(currentBtnIndex);
  }, [location.pathname]);
  
  const profile = useSelector(
    (state: RootState) => state.user.usersData
  )
  const isLoadingState = useSelector((state: RootState) => state.user.loading)
  
  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState)
  }
  
  const handleRedirectAccount = () => {
    customNavigate(`${ROUTES.ACCOUNT}/${ROUTES.SETTINGS}`)
  }
  
  const setShowMessageImageToLeft = () => {
    if(profile?.role?.slug === SELLER && profile.status === VALID) {
      return profile?.noImagesLeftToUpload;
    }
  }
  
  const handleRedirectSession = () => {
    customNavigate(`${ROUTES.ACCOUNT}/${ROUTES.UPLOAD}`)
  }

  return (
    <div className={styles.headerWrapper}>
      {/* Alert Banners */}
      {profile?.activeSession?.id && (
        <div className={styles.alertBanner} data-variant="warning">
          <div className={styles.alertContainer}>
            <div className={styles.alertLeft}>
              <span className={styles.alertPulse} />
              <span className={styles.alertLabel}>Active Session</span>
            </div>
            <Typography className={styles.alertMessage}>
              {t('session_upload_active', { number: setShowMessageImageToLeft() })}
            </Typography>
            <button className={styles.alertBtn} onClick={() => handleRedirectSession()}>
              {t('continue')}
              <ArrowForwardIcon />
            </button>
          </div>
        </div>
      )}
      
      {profile?.email && profile?.sellerExamination?.status === DECLINED && (
        <div className={styles.alertBanner} data-variant="error">
          <div className={styles.alertContainer}>
            <div className={styles.alertLeft}>
              <span className={styles.alertPulse} />
              <span className={styles.alertLabel}>Alert</span>
            </div>
            <Typography className={styles.alertMessage}>
              {t('examination_failed')}
            </Typography>
          </div>
        </div>
      )}
      
      {((profile?.role?.slug === SELLER || profile?.role?.slug === BUYER) && 
        (profile?.email && profile?.addresses?.length === 0)) && (
        <div className={styles.alertBanner} data-variant="info">
          <div className={styles.alertContainer}>
            <div className={styles.alertLeft}>
              <span className={styles.alertPulse} />
              <span className={styles.alertLabel}>Action Needed</span>
            </div>
            <Typography className={styles.alertMessage}>
              {t('header_profile_incomplete')}
            </Typography>
            <button className={styles.alertBtn} onClick={handleRedirectAccount}>
              {t('header_link_profile')}
              <ArrowForwardIcon />
            </button>
          </div>
        </div>
      )}

      {/* Main Header */}
      <AppBar 
        className={`${styles.header} ${scrolled ? styles.headerScrolled : ''}`} 
        component='nav' 
        position='sticky'
      >
        <div className={styles.headerContent}>
          {/* Logo */}
          <div 
            className={styles.logoBox}
            onClick={() => {
              customNavigate(NavigationEnum.HOME);
              setSelectedButton(1);
            }}
          >
            <img
              src={constants.logo_layerstock}
              alt='Layerstock'
              className={styles.logoImg}
            />
          </div>

          {/* Desktop Navigation */}
          <nav className={styles.mainNav}>
            {navItems.map((item: any) => (
              <Fragment key={`${item.title}-${item.value}`}>
                <NavButton
                  setSelectedButton={setSelectedButton}
                  title={item.title}
                  value={item.value}
                  listOfItems={item.listOfItems}
                  selected={item.value === selectedButton}
                  hovered={item.value === hoveredButton}
                  buttonOnMouseEnter={setHoveredButton}
                  to={item.to as NavigationEnum}
                  params={item?.params?.toUpperCase() || ''}
                />
              </Fragment>
            ))}
          </nav>

          {/* Right Side */}
          <div className={styles.headerRight}>
            {/* Pricing Link */}
            <div className={styles.pricingLink}>
              <NavButton
                setSelectedButton={setSelectedButton}
                title={t('Plans & Prices').toUpperCase()}
                value={5}
                selected={selectedButton === 5}
                hovered={hoveredButton === 5}
                buttonOnMouseEnter={setHoveredButton}
                to={NavigationEnum.PLANS_PRICES}
              />
            </div>

            {/* Divider */}
            <span className={styles.headerDivider} />

            {/* Utilities Group */}
            <div className={styles.utilitiesGroup}>
              <div className={styles.utilitySlot}>
                <Language />
              </div>
              
              {isAuthenticated && profile && (
                <div className={styles.utilitySlot}>
                  <NotificationsNavbar />
                </div>
              )}
            </div>

            {/* Account CTA */}
            {isLoadingState ? (
              <div className={styles.loadingBox}>
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              </div>
            ) : (
              <button
                className={`${styles.accountCta} ${selectedButton === 6 ? styles.accountCtaActive : ''}`}
                onClick={() => {
                  if (isAuthenticated && profile) {
                    customNavigate(NavigationEnum.ACCOUNT)
                  } else {
                    customNavigate(NavigationEnum.LOGIN)
                  }
                  setSelectedButton(6);
                }}
              >
                <PersonIcon className={styles.accountCtaIcon} />
                <span>{t(isAuthenticated && profile ? 'Account' : 'Login')}</span>
              </button>
            )}

            {/* Mobile Menu */}
            <button
              className={`${styles.menuTrigger} ${mobileOpen ? styles.menuTriggerActive : ''}`}
              onClick={handleDrawerToggle}
              aria-label="Toggle menu"
            >
              <div className={styles.menuTriggerInner}>
                <span />
                <span />
              </div>
            </button>
          </div>
        </div>

        {/* Bottom accent line */}
        <div className={styles.headerAccent} />
      </AppBar>

      {/* Mobile Drawer */}
      {matchesMobile && (
        <MobileDrawer
          items={navItems}
          open={mobileOpen}
          handleDrawerToggle={handleDrawerToggle}
          isAuthenticated={isAuthenticated}
          profile={profile}
          customNavigate={customNavigate}
          selectedButton={selectedButton}
          setSelectedButton={setSelectedButton}
        />
      )}
    </div>
  )
}